// Invoer: toetsenbord, aanraakknoppen, joystick, claxon en nachtmodus.
// Methoden worden op BezorgerGame.prototype gezet (zie game/mixins.js).
export class InputMixin {
  calculateIsNight() {
    if (this.nightMode === 'dark') return true;
    if (this.nightMode === 'light') return false;

    // Auto: Check system preference first, fallback to 20:00 - 07:00 local time
    const sysDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const hour = new Date().getHours();
    const timeDark = (hour >= 20 || hour < 7);
    return sysDark || timeDark;
  }

  setNightMode(mode) {
    this.nightMode = mode;
    this.isNight = this.calculateIsNight();
    document.documentElement.classList.toggle('dark', this.isNight);
  }

  startHorn() {
    this.sound.init();
    const isVan = (this.scooter && this.scooter.vehicleType === 'van');
    this.sound.startHorn(isVan);
    this.checkSpookCat();
    if (this.map && typeof this.map.checkHornReactionToCars === 'function') {
      this.map.checkHornReactionToCars(this.scooter, this.sound);
    }
    const b1 = document.getElementById('btn-horn');
    const b2 = document.getElementById('btn-touch-horn');
    if (b1) b1.classList.add('scale-110', 'ring-4', 'ring-amber-300', 'bg-amber-600');
    if (b2) b2.classList.add('scale-110', 'ring-4', 'ring-amber-300', 'bg-amber-600');
  }

  stopHorn() {
    this.sound.stopHorn();
    const b1 = document.getElementById('btn-horn');
    const b2 = document.getElementById('btn-touch-horn');
    if (b1) b1.classList.remove('scale-110', 'ring-4', 'ring-amber-300', 'bg-amber-600');
    if (b2) b2.classList.remove('scale-110', 'ring-4', 'ring-amber-300', 'bg-amber-600');
  }

  setupInputs() {
    // Keyboard inputs
    window.addEventListener('keydown', (e) => {
      // First user interaction initializes audio
      this.sound.init();

      const k = e.key.toLowerCase();
      // Typen in een veld of een dialoog openstaan: niet sturen.
      if (e.target instanceof HTMLElement && e.target.closest('input, textarea, [role="dialog"]')) return;
      // Staat er een scherm open (pauze, garage, ...), dan regelt de interface de toetsen.
      if (!document.getElementById('screens')?.classList.contains('hidden')) return;
      const playing = (this.mode === 'shift' || this.mode === 'freeride') && !this.paused;

      if (k === 'escape' || k === 'p') {
        if (this.mode !== 'menu') {
          e.preventDefault();
          this.paused ? this.resume() : this.pause();
        }
        return;
      }
      if (!playing || e.repeat) return;
      if (k === 'enter') {
        e.preventDefault();
        this.ui?.acceptFirstOffer();
        return;
      }
      if (k === 'x') {
        this.ui?.declineFirstOffer();
        return;
      }
      if (k.startsWith('arrow') || k === ' ') e.preventDefault();

      if (k === 'arrowup' || k === 'w') this.scooter.keys.up = true;
      if (k === 'arrowdown' || k === 's') this.scooter.keys.down = true;
      if (k === 'arrowleft' || k === 'a') this.scooter.keys.left = true;
      if (k === 'arrowright' || k === 'd') this.scooter.keys.right = true;
      if (k === ' ' || k === 'shift') {
        this.scooter.keys.handbrake = true;
        e.preventDefault();
      }
      if (k === 'h' || k === 'b') {
        this.startHorn();
      }
    });

    window.addEventListener('keyup', (e) => {
      const k = e.key.toLowerCase();
      if (k === 'arrowup' || k === 'w') this.scooter.keys.up = false;
      if (k === 'arrowdown' || k === 's') this.scooter.keys.down = false;
      if (k === 'arrowleft' || k === 'a') this.scooter.keys.left = false;
      if (k === 'arrowright' || k === 'd') this.scooter.keys.right = false;
      if (k === ' ' || k === 'shift') this.scooter.keys.handbrake = false;
      if (k === 'h' || k === 'b') {
        this.stopHorn();
      }
    });

    // Touch / Mobile on-screen pedalen (Gas, Rem, Drift)
    this.bindTouchButton('btn-gas', () => { this.scooter.keys.up = true; }, () => { this.scooter.keys.up = false; });
    this.bindTouchButton('btn-brake', () => { this.scooter.keys.down = true; }, () => { this.scooter.keys.down = false; });
    this.bindTouchButton('btn-handbrake', () => { this.scooter.keys.handbrake = true; }, () => { this.scooter.keys.handbrake = false; });

    // Modus A: Grote Arcade Stuurknoppen (Links & Rechts)
    this.bindTouchButton('btn-steer-left', () => { this.scooter.keys.left = true; }, () => { this.scooter.keys.left = false; });
    this.bindTouchButton('btn-steer-right', () => { this.scooter.keys.right = true; }, () => { this.scooter.keys.right = false; });

    // Modus B: 360° Richting-Joystick
    this.setupJoystick();

    // Eén duim: zwevende joystick die ook gas geeft
    this.setupOneThumb();

    // Knoppen, 360°-joystick of één duim (instelling)
    this.applyControlMode(this.controlMode || 'buttons');

    // Toeter knoppen (variabele tijdsduur: zolang ingedrukt gehouden)
    const bindHorn = (id) => {
      const btn = document.getElementById(id);
      if (!btn) return;

      const handleStart = (e) => {
        e.preventDefault();
        this.startHorn();
      };

      const handleEnd = (e) => {
        e.preventDefault();
        this.stopHorn();
      };

      btn.addEventListener('pointerdown', handleStart);
      btn.addEventListener('pointerup', handleEnd);
      btn.addEventListener('pointercancel', handleEnd);
      btn.addEventListener('pointerleave', () => {
        if (this.sound.isHonking) this.stopHorn();
      });

      // Touch events support
      btn.addEventListener('touchstart', handleStart, { passive: false });
      btn.addEventListener('touchend', handleEnd, { passive: false });
      btn.addEventListener('touchcancel', handleEnd, { passive: false });

      btn.addEventListener('mousedown', handleStart);
      btn.addEventListener('mouseup', handleEnd);
    };
    bindHorn('btn-horn');
    bindHorn('btn-touch-horn');
    bindHorn('btn-left-horn');
  }

  applyControlMode(mode) {
    this.controlMode = mode;
    const btnContainer = document.getElementById('controls-mode-buttons');
    const stickContainer = document.getElementById('controls-mode-joystick');
    const oneThumb = mode === 'eenduim';
    const isTouch = document.documentElement.classList.contains('touch');
    document.getElementById('onethumb-zone')?.classList.toggle('hidden', !oneThumb || !isTouch);
    document.getElementById('steering')?.classList.toggle('hidden', oneThumb);
    document.getElementById('pedals')?.classList.toggle('hidden', oneThumb);
    if (this.scooter) {
      this.scooter.targetDirectAngle = null;
      this.scooter.analogThrottle = 0;
    }

    if (oneThumb) {
      if (this.scooter) this.scooter.directStickActive = true;
    } else if (mode === 'joystick') {
      if (btnContainer) btnContainer.classList.add('hidden');
      if (stickContainer) stickContainer.classList.remove('hidden');
      if (this.scooter) {
        this.scooter.directStickActive = true;
      }
    } else {
      if (btnContainer) btnContainer.classList.remove('hidden');
      if (stickContainer) stickContainer.classList.add('hidden');
      if (this.scooter) {
        this.scooter.directStickActive = false;
        this.scooter.targetDirectAngle = null;
      }
    }
  }

  setupJoystick() {
    const base = document.getElementById('joystick-base');
    const knob = document.getElementById('joystick-knob');
    if (!base || !knob) return;

    let isDragging = false;
    let centerX = 0;
    let centerY = 0;
    const maxRadius = 44; // Maximale uitslag van de 360° joystick in pixels

    const updatePosition = (clientX, clientY) => {
      let dx = clientX - centerX;
      let dy = clientY - centerY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > maxRadius) {
        dx = (dx / dist) * maxRadius;
        dy = (dy / dist) * maxRadius;
      }

      // Vloeiende 360° verplaatsing van de knop
      knob.style.transform = `translate(${dx}px, ${dy}px)`;

      // Bij merkbare beweging: bereken exacte gewenste hoek en analoge stuurhoek
      if (dist > 7) {
        const stickAngle = Math.atan2(dy, dx);
        this.scooter.directStickActive = (this.controlMode === 'joystick');
        this.scooter.targetDirectAngle = stickAngle;
        this.scooter.analogSteer = Math.max(-1.0, Math.min(1.0, dx / maxRadius));
      } else {
        this.scooter.targetDirectAngle = null;
        this.scooter.analogSteer = 0;
      }
    };

    const resetJoystick = () => {
      if (!isDragging) return;
      isDragging = false;
      knob.style.transform = 'translate(0px, 0px)';
      base.classList.remove('ring-4', 'ring-amber-400/40');
      this.scooter.targetDirectAngle = null;
      this.scooter.analogSteer = 0;
      this.scooter.analogThrottle = 0;
    };

    const startDrag = (clientX, clientY) => {
      this.sound.init();
      isDragging = true;
      base.classList.add('ring-4', 'ring-amber-400/40');
      const baseRect = base.getBoundingClientRect();
      centerX = baseRect.left + baseRect.width / 2;
      centerY = baseRect.top + baseRect.height / 2;
      updatePosition(clientX, clientY);
    };

    // 1. Pointer Events (Desktop muis & moderne aanraakschermen)
    base.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      try { base.setPointerCapture(e.pointerId); } catch (_) {}
      startDrag(e.clientX, e.clientY);
    });

    window.addEventListener('pointermove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      updatePosition(e.clientX, e.clientY);
    }, { passive: false });

    window.addEventListener('pointerup', (e) => {
      if (!isDragging) return;
      try { base.releasePointerCapture(e.pointerId); } catch (_) {}
      resetJoystick();
    });

    window.addEventListener('pointercancel', (e) => {
      if (!isDragging) return;
      try { base.releasePointerCapture(e.pointerId); } catch (_) {}
      resetJoystick();
    });

    // 2. Touch Events Fallback (voor iOS Safari en embedded iframes)
    base.addEventListener('touchstart', (e) => {
      if (e.touches && e.touches[0]) {
        e.preventDefault();
        startDrag(e.touches[0].clientX, e.touches[0].clientY);
      }
    }, { passive: false });

    window.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      if (e.touches && e.touches[0]) {
        e.preventDefault();
        updatePosition(e.touches[0].clientX, e.touches[0].clientY);
      }
    }, { passive: false });

    window.addEventListener('touchend', (e) => {
      if (isDragging) {
        e.preventDefault();
        resetJoystick();
      }
    }, { passive: false });

    window.addEventListener('touchcancel', (e) => {
      if (isDragging) resetJoystick();
    });

    // 3. Mouse Events Fallback
    base.addEventListener('mousedown', (e) => {
      e.preventDefault();
      startDrag(e.clientX, e.clientY);
    });

    window.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      updatePosition(e.clientX, e.clientY);
    });

    window.addEventListener('mouseup', () => {
      if (isDragging) resetJoystick();
    });
  }

  /**
   * Eén duim: waar je je duim neerzet verschijnt een joystick. De richting is waar de scooter heen rijdt,
   * de afstand tot het midden is het gas. Loslaten: gas los, de scooter rolt uit.
   */
  setupOneThumb() {
    const zone = document.getElementById('onethumb-zone');
    const base = document.getElementById('onethumb-base');
    const knob = document.getElementById('onethumb-knob');
    if (!zone || !base || !knob) return;
    const MAX = 60;
    const DEAD = 8;
    let pointerId = null;
    let cx = 0;
    let cy = 0;

    const move = (x, y) => {
      let dx = x - cx;
      let dy = y - cy;
      const dist = Math.hypot(dx, dy);
      if (dist > MAX) {
        dx = (dx / dist) * MAX;
        dy = (dy / dist) * MAX;
      }
      knob.style.transform = `translate(${dx}px, ${dy}px)`;
      const s = this.scooter;
      if (dist > DEAD) {
        s.directStickActive = true;
        s.targetDirectAngle = Math.atan2(dy, dx);
        s.analogThrottle = Math.min(1, (Math.min(dist, MAX) - DEAD) / (MAX * 0.7 - DEAD));
      } else {
        s.targetDirectAngle = null;
        s.analogThrottle = 0;
      }
    };
    const end = (e) => {
      if (pointerId === null || (e && e.pointerId !== pointerId)) return;
      pointerId = null;
      base.classList.add('hidden');
      zone.classList.remove('onethumb-active');
      knob.style.transform = '';
      this.scooter.targetDirectAngle = null;
      this.scooter.analogThrottle = 0;
    };

    zone.addEventListener('pointerdown', (e) => {
      if (pointerId !== null) return;
      e.preventDefault();
      this.sound.init();
      pointerId = e.pointerId;
      try {
        zone.setPointerCapture(e.pointerId);
      } catch (_) {}
      const r = zone.getBoundingClientRect();
      cx = e.clientX;
      cy = e.clientY;
      base.style.left = `${cx - r.left}px`;
      base.style.top = `${cy - r.top}px`;
      base.classList.remove('hidden');
      zone.classList.add('onethumb-active');
      move(e.clientX, e.clientY);
    });
    zone.addEventListener('pointermove', (e) => {
      if (e.pointerId !== pointerId) return;
      e.preventDefault();
      move(e.clientX, e.clientY);
    });
    zone.addEventListener('pointerup', end);
    zone.addEventListener('pointercancel', end);
    this.releaseOneThumb = () => end();
  }

  bindTouchButton(id, onDown, onUp) {
    const el = document.getElementById(id);
    if (!el) return;
    let isActive = false;

    const startHandler = (e) => {
      if (e.cancelable) e.preventDefault();
      if (isActive) return;
      isActive = true;
      el.classList.add('brightness-125', 'ring-2', 'ring-amber-400/80');
      if (this.sound) this.sound.init();
      onDown();
    };

    const endHandler = (e) => {
      if (e.cancelable) e.preventDefault();
      if (!isActive) return;
      isActive = false;
      el.classList.remove('brightness-125', 'ring-2', 'ring-amber-400/80');
      onUp();
    };

    el.addEventListener('pointerdown', startHandler);
    el.addEventListener('pointerup', endHandler);
    el.addEventListener('pointercancel', endHandler);
    el.addEventListener('pointerleave', endHandler);

    el.addEventListener('touchstart', startHandler, { passive: false });
    el.addEventListener('touchend', endHandler, { passive: false });
    el.addEventListener('touchcancel', endHandler, { passive: false });
  }

  checkSpookCat() {
    if (!this.map || !this.map.cats) return;
    let spookedAny = false;
    for (const cat of this.map.cats) {
      if (cat.dead) continue;
      const dx = this.scooter.x - cat.x;
      const dy = this.scooter.y - cat.y;
      if (dx * dx + dy * dy < 280 * 280) {
        cat.state = 'fleeing';
        cat.stateTimer = 1.8;
        cat.angle = Math.atan2(cat.y - this.scooter.y, cat.x - this.scooter.x);
        cat.speechText = '🙀 "Miauw!"';
        cat.speechTimer = 2.0;
        spookedAny = true;
      }
    }
    if (spookedAny) {
      this.sound.playCatMeow();
    }

    // AI-auto's in de buurt reageren op de claxon van de bezorger
    if (this.map && this.map.checkHornReactionToCars) {
      this.map.checkHornReactionToCars(this.scooter, this.sound);
    }
  }

}
